import { extractId, normalizeKind, worldSizeToScreen, worldToScreen } from "./camera";
import { drawHealthBar, drawLeaderCrown, leaderCrownHeight } from "./drawing";
import { drawHeart, drawPlayer, drawZombie } from "./entity_drawers";
import {
  cleanupDamageFlash,
  damageFlashColor,
  registerDamageFlash
} from "./entity_damage_flash";
import { drawFittedName } from "./entity_names";
import { renderOrder } from "./entity_order";
import type { Camera, SnapshotEntity } from "./types";
import { drawDamageZone, drawOrbitingBall, drawProjectile } from "./weapon_drawers";

const ENTITY_FADE_OUT_MS = 160;
const ENTITY_FADE_IN_MS = ENTITY_FADE_OUT_MS;

export type EntityFadeState = {
  previous: Map<string, SnapshotEntity>;
  fading: Map<string, FadingEntity>;
  appearing: Map<string, AppearingEntity>;
};

type FadingEntity = {
  entity: SnapshotEntity;
  startedAt: number;
  durationMs: number;
};

type AppearingEntity = {
  startedAt: number;
  durationMs: number;
};

type RenderEntity = {
  entity: SnapshotEntity;
  alpha: number;
  fading: boolean;
};

export function createEntityFadeState(): EntityFadeState {
  return {
    previous: new Map(),
    fading: new Map(),
    appearing: new Map()
  };
}

export function resetEntityFadeState(state: EntityFadeState): void {
  state.previous.clear();
  state.fading.clear();
  state.appearing.clear();
}

export function renderEntities(
  context: CanvasRenderingContext2D,
  entities: SnapshotEntity[],
  camera: Camera,
  width: number,
  height: number,
  fadeState: EntityFadeState,
  localPlayerId?: unknown
): boolean {
  void localPlayerId;

  const now = performance.now();
  const sorted = [...entities].sort((a, b) => renderOrder(a) - renderOrder(b));
  const renderEntities = buildRenderEntities(fadeState, sorted, now);
  let visibleDamage = false;

  for (const item of renderEntities) {
    if (item.alpha <= 0) {
      continue;
    }

    let damageEvent = false;

    if (!item.fading) {
      damageEvent = registerDamageFlash(item.entity, now);
    }

    context.save();
    context.globalAlpha *= item.alpha;

    const visible = drawEntityBody(context, item.entity, camera, width, height, now);

    context.restore();

    if (!item.fading && damageEvent && visible && isDamageReceiver(item.entity)) {
      visibleDamage = true;
    }
  }

  cleanupDamageFlash(now, entities);

  for (const item of renderEntities) {
    if (item.alpha <= 0) {
      continue;
    }

    context.save();
    context.globalAlpha *= item.alpha;
    drawEntityOverlay(context, item.entity, camera, width, height);
    context.restore();
  }

  return visibleDamage;
}

function buildRenderEntities(
  fadeState: EntityFadeState,
  entities: SnapshotEntity[],
  now: number
): RenderEntity[] {
  const currentKeys = new Set<string>();

  for (const entity of entities) {
    const key = entityKey(entity);
    currentKeys.add(key);
    fadeState.fading.delete(key);

    if (
      shouldFadeIn(entity) &&
      !fadeState.previous.has(key) &&
      !fadeState.appearing.has(key)
    ) {
      fadeState.appearing.set(key, {
        startedAt: now,
        durationMs: ENTITY_FADE_IN_MS
      });
    }
  }

  for (const [key, appearing] of fadeState.appearing) {
    if (!currentKeys.has(key) || now - appearing.startedAt >= appearing.durationMs) {
      fadeState.appearing.delete(key);
    }
  }

  for (const [key, fading] of fadeState.fading) {
    if (now - fading.startedAt >= fading.durationMs) {
      fadeState.fading.delete(key);
    }
  }

  for (const [key, previousEntity] of fadeState.previous) {
    if (currentKeys.has(key) || fadeState.fading.has(key) || !shouldFadeOut(previousEntity)) {
      continue;
    }

    fadeState.fading.set(key, {
      entity: cloneEntity(previousEntity),
      startedAt: now,
      durationMs: ENTITY_FADE_OUT_MS
    });
  }

  fadeState.previous.clear();

  for (const entity of entities) {
    fadeState.previous.set(entityKey(entity), cloneEntity(entity));
  }

  const result: RenderEntity[] = entities.map((entity) => {
    const appearing = fadeState.appearing.get(entityKey(entity));
    const alpha = appearing
      ? Math.max(0, Math.min(1, (now - appearing.startedAt) / appearing.durationMs))
      : 1;

    return {
      entity,
      alpha,
      fading: false
    };
  });

  for (const fading of fadeState.fading.values()) {
    const progress = (now - fading.startedAt) / fading.durationMs;
    const alpha = Math.max(0, Math.min(1, 1 - progress));

    if (alpha > 0) {
      result.push({
        entity: fading.entity,
        alpha,
        fading: true
      });
    }
  }

  result.sort((a, b) => renderOrder(a.entity) - renderOrder(b.entity));

  return result;
}

function drawEntityBody(
  context: CanvasRenderingContext2D,
  entity: SnapshotEntity,
  camera: Camera,
  width: number,
  height: number,
  now: number
): boolean {
  const screen = worldToScreen(entity.position.x, entity.position.y, camera, width, height);
  const radius = worldSizeToScreen(entity.radius, camera);
  const kind = normalizeKind(entity.kind);

  if (!isVisible(screen.x, screen.y, radius, width, height)) {
    return false;
  }

  if (kind === "damagezone") {
    drawDamageZone(context, screen.x, screen.y, radius);
    return true;
  }

  if (kind === "projectile") {
    drawProjectile(context, screen.x, screen.y, radius, entity.direction);
    return true;
  }

  if (kind === "orbitingball") {
    drawOrbitingBall(context, screen.x, screen.y, radius);
    return true;
  }

  if (kind === "heart") {
    drawHeart(context, screen.x, screen.y, radius, entity.heart_tier ?? entity.heartTier);
    return true;
  }

  if (kind === "zombie") {
    drawZombie(context, screen.x, screen.y, radius, entity, damageFlashColor(entity, now));
    return true;
  }

  if (kind === "player" || kind === "bot") {
    drawPlayer(context, screen.x, screen.y, radius, entity, damageFlashColor(entity, now));
    return true;
  }

  return true;
}

function drawEntityOverlay(
  context: CanvasRenderingContext2D,
  entity: SnapshotEntity,
  camera: Camera,
  width: number,
  height: number
): void {
  const kind = normalizeKind(entity.kind);

  if (kind !== "zombie" && kind !== "player" && kind !== "bot") {
    return;
  }

  const screen = worldToScreen(entity.position.x, entity.position.y, camera, width, height);
  const radius = worldSizeToScreen(entity.radius, camera);

  if (!isVisible(screen.x, screen.y, radius, width, height)) {
    return;
  }

  const isLeader = Boolean(entity.is_leader ?? entity.isLeader);
  let levelY = screen.y - radius - 14;

  if (kind === "player" || kind === "bot") {
    if (entity.name) {
      drawFittedName(context, screen.x, screen.y, radius, entity);
    }

    if (isLeader) {
      const crownSize = Math.max(24, radius * 0.6);
      const crownBottomY = screen.y - radius + 3;

      drawLeaderCrown(context, screen.x, crownBottomY, crownSize);
      levelY = crownBottomY - leaderCrownHeight(crownSize) - 8;
    }
  }

  drawLevelLabel(context, screen.x, levelY, entity.level ?? 1);

  if (kind === "player" || kind === "bot") {
    drawHealthBar(
      context,
      screen.x,
      screen.y + radius + 10,
      Math.max(46, radius * 1.8),
      entity.hp ?? 0,
      entity.max_hp ?? entity.maxHp ?? 1
    );

    return;
  }

  drawHealthBar(
    context,
    screen.x,
    screen.y + radius + 10,
    Math.max(48, radius * 1.55),
    entity.hp ?? 0,
    entity.max_hp ?? entity.maxHp ?? 1
  );
}

function drawLevelLabel(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  level: number
): void {
  const text = `Lv ${Math.max(1, Math.floor(level))}`;

  context.save();
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = "900 13px system-ui, sans-serif";
  context.lineWidth = 4;
  context.strokeStyle = "rgba(255,255,255,0.9)";
  context.fillStyle = "#111111";
  context.strokeText(text, x, y);
  context.fillText(text, x, y);
  context.restore();
}

function shouldFadeIn(entity: SnapshotEntity): boolean {
  const kind = normalizeKind(entity.kind);

  return kind === "player" || kind === "bot";
}

function shouldFadeOut(entity: SnapshotEntity): boolean {
  const kind = normalizeKind(entity.kind);

  return kind === "projectile" || kind === "player" || kind === "bot" || kind === "zombie";
}

function cloneEntity(entity: SnapshotEntity): SnapshotEntity {
  return {
    ...entity,
    position: {
      x: entity.position.x,
      y: entity.position.y
    },
    direction: entity.direction
      ? {
          x: entity.direction.x,
          y: entity.direction.y
        }
      : entity.direction
  };
}

function entityKey(entity: SnapshotEntity): string {
  return `${normalizeKind(entity.kind)}:${String(extractId(entity.id) ?? fallbackEntityId(entity))}`;
}

function fallbackEntityId(entity: SnapshotEntity): string {
  return `${entity.position.x}:${entity.position.y}:${entity.radius}`;
}

function isVisible(
  x: number,
  y: number,
  radius: number,
  width: number,
  height: number
): boolean {
  const margin = 100;

  return !(
    x + radius < -margin ||
    x - radius > width + margin ||
    y + radius < -margin ||
    y - radius > height + margin
  );
}

function isDamageReceiver(entity: SnapshotEntity): boolean {
  const kind = normalizeKind(entity.kind);
  return kind === "player" || kind === "bot" || kind === "zombie";
}